import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useCurrency } from '../../context/CurrencyContext';
import { useAuth } from '../../context/AuthContext';
import { fetchActivePlans, formatStorage } from '../../services/plansService';
import { activatePlan } from '../../services/subscriptionService';

const {
  FiCheck, FiX, FiShield, FiZap, FiAward,
  FiRefreshCw, FiDatabase, FiStar, FiLoader, FiAlertTriangle,
  FiPackage, FiChevronDown, FiChevronUp, FiHardDrive, FiArrowRight, FiInfo,
} = FiIcons;

const ICON_POOL  = [FiDatabase, FiZap, FiShield, FiAward, FiStar, FiPackage];
const COLOR_POOL = [
  { color: 'text-blue-500',   bg: 'bg-blue-50'   },
  { color: 'text-amber-500',  bg: 'bg-amber-50'  },
  { color: 'text-purple-500', bg: 'bg-purple-50' },
  { color: 'text-green-500',  bg: 'bg-green-50'  },
  { color: 'text-rose-500',   bg: 'bg-rose-50'   },
  { color: 'text-cyan-500',   bg: 'bg-cyan-50'   },
];

function normalizePlan(plan, index) {
  const { color, bg } = COLOR_POOL[index % COLOR_POOL.length];
  return { ...plan, icon: ICON_POOL[index % ICON_POOL.length], color, bg };
}

function billingLabel(cycle, price) {
  if (price === 0 || cycle === 'forever') return 'Free forever';
  if (cycle === 'year') return 'per year';
  return 'per month';
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const LegacyPlanComparison = ({ currentPlan = 'free', onUpgrade }) => {
  // ★ Pull convertAmount so every price is properly converted from USD
  const { formatCurrency, convertAmount, currency, getExchangeRate, ratesReady } = useCurrency();
  const { user } = useAuth();

  const [plans,        setPlans]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [activating,   setActivating]   = useState(false);
  const [activateErr,  setActivateErr]  = useState(null);
  const [isSuccess,    setIsSuccess]    = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [billing,      setBilling]      = useState('annual'); // Added billing toggle

  // ★ Centralised price formatter: converts USD → active currency, then formats
  const fmtPrice = useCallback((usd) => {
    if (usd === 0) return 'Free';
    return formatCurrency(convertAmount(usd, 'USD'));
  }, [formatCurrency, convertAmount]);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const raw = await fetchActivePlans();
      if (!raw || raw.length === 0) throw new Error('No active plans returned from server.');
      setPlans(raw.map(normalizePlan));
    } catch (err) {
      setLoadError(err.message || 'Failed to load plans.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const handleSelectPlan = useCallback(async (plan) => {
    if (!user) { onUpgrade?.('__auth_required__'); return; }
    setCheckoutPlan(plan);
    setActivateErr(null);
    setIsSuccess(false);
  }, [user, onUpgrade]);

  const handleConfirmActivation = useCallback(async () => {
    if (!checkoutPlan || checkoutPlan === 'trust-deed') return;
    setActivating(true);
    setActivateErr(null);
    try {
      await activatePlan(checkoutPlan);
      setIsSuccess(true);
      setTimeout(() => {
        onUpgrade?.(checkoutPlan.plan_key);
        setIsSuccess(false);
        setCheckoutPlan(null);
      }, 2200);
    } catch (err) {
      setActivateErr(err.message || 'Activation failed. Please try again.');
    } finally {
      setActivating(false);
    }
  }, [checkoutPlan, onUpgrade]);

  const closeModal = () => {
    if (activating) return;
    setCheckoutPlan(null);
    setActivateErr(null);
    setIsSuccess(false);
  };

  if (loading) return <LoadingState />;
  if (loadError) return <ErrorState message={loadError} onRetry={loadPlans} />;

  // ★ Currency indicator chip shown in header when non-USD
  const isNonUSD = currency !== 'USD';
  const rate = getExchangeRate('USD', currency);
  const rateStr = rate >= 1000
    ? rate.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <div className="space-y-12 relative">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-accent-gold mb-4">
          The Digital Estate Suite
        </div>
        <h2 className="text-3xl md:text-5xl font-serif text-accent-navy mb-6">
          Choose Your Protection Tier
        </h2>
        <p className="text-sm text-text-muted font-light leading-relaxed">
          Select the level of care required to preserve your digital legacy.
          All plans include AES-256 encryption and perpetual access for designated heirs.
        </p>
        {/* ★ Currency conversion notice */}
        {isNonUSD && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-xs text-amber-700 font-medium">
            <span className={`w-2 h-2 rounded-full ${ratesReady ? 'bg-green-500' : 'bg-amber-500'}`} />
            Prices shown in {currency} · 1 USD = {rateStr} {currency} ·{' '}
            <span className="font-bold">{ratesReady ? 'live rate' : 'est. rate'}</span>
          </div>
        )}
      </div>

      {/* Pricing Toggle */}
      <div className="flex justify-center mb-2">
        <div className="flex items-center gap-2 bg-brand-light border border-brand-dark rounded-2xl p-1.5">
          {['monthly', 'annual'].map(b => (
            <button
              key={b}
              onClick={() => setBilling(b)}
              className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                billing === b
                  ? 'bg-accent-navy text-white shadow-lg shadow-accent-navy/20'
                  : 'text-text-muted hover:text-accent-navy'
              }`}
            >
              {b === 'monthly' ? 'Monthly' : 'Annual · Save 20%'}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile: Stacked Cards */}
      <div className="grid grid-cols-1 gap-6 md:hidden">
        {plans.map((plan, i) => (
          <MobilePlanCard
            key={plan.id} plan={plan} index={i}
            isCurrent={currentPlan === plan.plan_key}
            isExpanded={expandedCard === plan.id}
            onToggleExpand={() => setExpandedCard(p => p === plan.id ? null : plan.id)}
            onSelect={() => handleSelectPlan(plan)}
            fmtPrice={fmtPrice}
            billing={billing}
          />
        ))}
      </div>

      {/* Desktop: Comparison Matrix */}
      <div className="hidden md:block overflow-hidden bg-white rounded-[40px] border border-brand-dark shadow-sm">
        <ComparisonTable
          plans={plans}
          currentPlan={currentPlan}
          onSelect={handleSelectPlan}
          fmtPrice={fmtPrice}
          billing={billing}
        />
        <div className="bg-brand-light p-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-accent-gold shadow-sm">
              <SafeIcon icon={FiShield} className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-accent-navy">Generational Trust Fund</h4>
              <p className="text-xs text-text-muted">
                A portion of every paid plan is legally held to ensure perpetual uptime of your digital estate.
              </p>
            </div>
          </div>
          <button onClick={() => setCheckoutPlan('trust-deed')}
            className="text-xs font-black uppercase tracking-widest text-accent-gold hover:text-accent-navy transition-colors">
            View Trust Deed
          </button>
        </div>
      </div>

      {/* Checkout Modal */}
      <AnimatePresence>
        {checkoutPlan && checkoutPlan !== 'trust-deed' && createPortal(
          <CheckoutModal
            plan={checkoutPlan}
            activating={activating}
            activateErr={activateErr}
            isSuccess={isSuccess}
            isLoggedIn={!!user}
            onConfirm={handleConfirmActivation}
            onClose={closeModal}
            fmtPrice={fmtPrice}
            billing={billing}
          />,
          document.body
        )}
      </AnimatePresence>

      {/* Trust Deed Modal */}
      <AnimatePresence>
        {checkoutPlan === 'trust-deed' && createPortal(
          <TrustDeedModal onClose={closeModal} />,
          document.body
        )}
      </AnimatePresence>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPARISON TABLE
// ─────────────────────────────────────────────────────────────────────────────
function ComparisonTable({ plans, currentPlan, onSelect, fmtPrice, billing }) {
  const allFeatures = [...new Set(plans.flatMap(p => p.features || []))];
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <th className="p-10 text-left align-bottom w-1/4">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-accent-gold mb-2">Feature Suite</div>
            <div className="text-sm font-bold text-text-muted">Digital Asset Protection</div>
          </th>
          {plans.map(plan => (
            <PlanHeader key={plan.id} plan={plan}
              isCurrent={currentPlan === plan.plan_key}
              onSelect={() => onSelect(plan)}
              fmtPrice={fmtPrice}
              billing={billing} />
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-brand-light">
        <FeatureRow label="Secure Storage" icon={FiHardDrive} plans={plans}
          renderCell={plan => <span className="text-sm font-bold text-accent-navy">{formatStorage(plan.storage_mb)}</span>} />
        {allFeatures.map((feat, i) => (
          <FeatureRow key={i} label={feat} icon={FiCheck} plans={plans}
            renderCell={plan => (
              plan.features.includes(feat)
                ? <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                : <SafeIcon icon={FiX}     className="w-5 h-5 text-text-muted/25 mx-auto" />
            )} />
        ))}
      </tbody>
    </table>
  );
}

function PlanHeader({ plan, isCurrent, onSelect, fmtPrice, billing }) {
  const { currency } = useCurrency();
  const isNonUSD = currency !== 'USD';
  const isMonthlyPlan = plan.billing_cycle === 'month';
  const displayPrice = (isMonthlyPlan && billing === 'annual') ? plan.price_usd * 0.8 : plan.price_usd;

  return (
    <th className={`p-10 text-center w-1/4 transition-all duration-300 ${plan.highlight ? 'bg-brand-light/50' : ''}`}>
      <div className={`w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center ${plan.bg} ${plan.color} shadow-sm border border-brand-dark/20`}>
        <SafeIcon icon={plan.icon} className="w-8 h-8" />
      </div>
      {plan.highlight && (
        <span className="bg-accent-gold text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4 inline-block">
          Recommended
        </span>
      )}
      <h3 className="text-2xl font-serif text-accent-navy mb-1">{plan.name}</h3>
      {plan.description && (
        <p className="text-[11px] font-bold text-text-muted uppercase tracking-widest mb-4">{plan.description}</p>
      )}
      <div className="mb-2">
        {/* ★ Converted price */}
        <span className="text-3xl font-serif text-accent-navy">{fmtPrice(displayPrice)}</span>
        <p className="text-[10px] text-text-muted font-bold uppercase mt-1">
          {billingLabel(plan.billing_cycle, displayPrice)}
        </p>
        {/* ★ USD reference when non-USD */}
        {isNonUSD && displayPrice > 0 && (
          <p className="text-[9px] text-text-muted mt-0.5">${displayPrice.toFixed(2)} USD</p>
        )}
      </div>
      <button onClick={onSelect} disabled={isCurrent}
        className={`w-full py-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
          isCurrent
            ? 'bg-brand-dark text-text-muted cursor-default'
            : plan.highlight
              ? 'bg-accent-gold text-white hover:bg-accent-gold/90 shadow-lg shadow-accent-gold/20'
              : 'bg-accent-navy text-white hover:bg-accent-navy/90'
        }`}>
        {isCurrent ? 'Current Plan' : 'Select Plan'}
      </button>
    </th>
  );
}

function FeatureRow({ label, icon, plans, renderCell }) {
  return (
    <tr className="group hover:bg-brand-light/20 transition-colors">
      <td className="p-6 pl-10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-brand-light flex items-center justify-center text-accent-navy opacity-40 group-hover:opacity-100 transition-opacity">
            <SafeIcon icon={icon} className="w-4 h-4" />
          </div>
          <span className="text-sm font-bold text-accent-navy">{label}</span>
        </div>
      </td>
      {plans.map(plan => (
        <td key={plan.id} className={`p-6 text-center ${plan.highlight ? 'bg-brand-light/30' : ''}`}>
          {renderCell(plan)}
        </td>
      ))}
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE PLAN CARD
// ─────────────────────────────────────────────────────────────────────────────
function MobilePlanCard({ plan, isCurrent, isExpanded, onToggleExpand, onSelect, fmtPrice, billing }) {
  const { currency } = useCurrency();
  const isNonUSD = currency !== 'USD';
  const isMonthlyPlan = plan.billing_cycle === 'month';
  const displayPrice = (isMonthlyPlan && billing === 'annual') ? plan.price_usd * 0.8 : plan.price_usd;

  return (
    <motion.div layout
      className={`rounded-[32px] border-2 overflow-hidden transition-all ${
        plan.highlight ? 'border-accent-gold bg-white shadow-xl shadow-accent-gold/10' : 'border-brand-dark bg-white'
      }`}>
      <div className={`bg-gradient-to-br ${plan.color || 'from-gray-400 to-gray-600'} p-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-white/20">
              <SafeIcon icon={plan.icon} className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-serif text-white">{plan.name}</h3>
              {plan.description && <p className="text-[10px] text-white/70 font-light">{plan.description}</p>}
            </div>
          </div>
          {plan.highlight && (
            <span className="text-[9px] px-2 py-1 bg-white/20 text-white rounded-full font-black uppercase tracking-widest">Popular</span>
          )}
        </div>
        <div className="mt-4">
          {/* ★ Converted price */}
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-3xl font-serif text-white">{fmtPrice(displayPrice)}</span>
            {plan.price_usd > 0 && (
              <span className="text-white/60 text-sm font-light">/{plan.billing_cycle === 'year' ? 'yr' : 'mo'}</span>
            )}
          </div>
          {/* ★ USD reference */}
          {isNonUSD && displayPrice > 0 && (
            <p className="text-[9px] text-white/50 mt-0.5">${displayPrice.toFixed(2)} USD</p>
          )}
        </div>
        <p className="text-[10px] text-white/60 mt-1">
          <SafeIcon icon={FiHardDrive} className="w-3 h-3 inline mr-1" />
          {formatStorage(plan.storage_mb)} secure storage
        </p>
      </div>

      <div className="p-6 space-y-4">
        <button onClick={onToggleExpand} className="flex items-center justify-between w-full text-left">
          <span className="text-[10px] font-black uppercase tracking-widest text-text-muted">
            {plan.features?.length || 0} Included Features
          </span>
          <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="w-4 h-4 text-text-muted" />
        </button>
        <AnimatePresence>
          {isExpanded && (
            <motion.ul initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden space-y-2">
              {(plan.features || []).map((feat, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-text-muted">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {feat}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
        <button onClick={onSelect} disabled={isCurrent}
          className={`w-full py-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
            isCurrent
              ? 'bg-brand-dark text-text-muted cursor-default'
              : plan.highlight
                ? 'bg-accent-gold text-white shadow-lg shadow-accent-gold/20'
                : 'bg-accent-navy text-white hover:bg-accent-navy/90'
          }`}>
          {isCurrent ? 'Current Plan' : 'Select Plan'}
        </button>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHECKOUT MODAL
// ─────────────────────────────────────────────────────────────────────────────
function CheckoutModal({ plan, activating, activateErr, isSuccess, isLoggedIn, onConfirm, onClose, fmtPrice, billing }) {
  const { currency } = useCurrency();
  const isNonUSD = currency !== 'USD';
  const isMonthlyPlan = plan.billing_cycle === 'month';
  const displayPrice = (isMonthlyPlan && billing === 'annual') ? plan.price_usd * 0.8 : plan.price_usd;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-md overflow-hidden border border-brand-dark">
        <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${plan.bg} ${plan.color} rounded-xl flex items-center justify-center border border-brand-dark/20`}>
              <SafeIcon icon={plan.icon || FiShield} className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-accent-navy">{plan.name}</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
                {plan.price_usd === 0 ? 'Free Forever' : `${fmtPrice(displayPrice)} ${billingLabel(plan.billing_cycle, displayPrice)}`}
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={activating}
            className="p-2 hover:bg-white rounded-full text-text-muted transition-colors disabled:opacity-40">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="p-8">
          <AnimatePresence mode="wait">
            {isSuccess && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="text-center py-6">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCheck} className="w-10 h-10" />
                </div>
                <h4 className="text-2xl font-serif text-accent-navy mb-2">Plan Activated!</h4>
                <p className="text-sm text-text-muted font-light">
                  Your <strong>{plan.name}</strong> is now active.{' '}
                  {formatStorage(plan.storage_mb)} of encrypted storage is ready.
                </p>
              </motion.div>
            )}
            {!isSuccess && (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="bg-brand-light rounded-2xl p-5 space-y-3 border border-brand-dark/30">
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">What's included</p>
                  <div className="flex items-center gap-2 text-sm font-bold text-accent-navy">
                    <SafeIcon icon={FiHardDrive} className="w-4 h-4 text-accent-gold" />
                    {formatStorage(plan.storage_mb)} encrypted storage
                  </div>
                  {(plan.features || []).slice(0, 4).map((feat, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-text-muted">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {feat}
                    </div>
                  ))}
                  {plan.features?.length > 4 && (
                    <p className="text-[10px] text-text-muted font-bold">+{plan.features.length - 4} more features included</p>
                  )}
                </div>

                {/* ★ Price summary with conversion */}
                <div className="p-4 bg-white rounded-2xl border-2 border-brand-dark">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-accent-navy">Total</span>
                    <div className="text-right">
                      <span className="text-xl font-serif text-accent-gold">
                        {plan.price_usd === 0 ? 'Free' : fmtPrice(displayPrice)}
                        {plan.price_usd > 0 && (
                          <span className="text-sm text-text-muted font-light ml-1">/{plan.billing_cycle === 'year' ? 'yr' : 'mo'}</span>
                        )}
                      </span>
                      {isNonUSD && displayPrice > 0 && (
                        <p className="text-[9px] text-text-muted mt-0.5">${displayPrice.toFixed(2)} USD</p>
                      )}
                    </div>
                  </div>
                </div>

                {!isLoggedIn && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                    <SafeIcon icon={FiInfo} className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 font-light">You need to be signed in to activate a plan.</p>
                  </div>
                )}
                {activateErr && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
                    <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700 font-light">{activateErr}</p>
                  </div>
                )}
                <button onClick={onConfirm} disabled={activating || !isLoggedIn}
                  className="w-full bg-accent-navy text-white py-4 rounded-xl font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-accent-gold transition-all shadow-lg shadow-accent-navy/10 disabled:opacity-60 disabled:cursor-not-allowed">
                  {activating
                    ? <><SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" /> Activating…</>
                    : <><SafeIcon icon={FiArrowRight} className="w-5 h-5" /> Activate {plan.name}</>
                  }
                </button>
                <p className="text-[10px] text-text-muted text-center font-light">
                  <SafeIcon icon={FiShield} className="w-3 h-3 inline mr-1 text-accent-gold" />
                  Secured with AES-256 encryption. Cancel anytime.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRUST DEED MODAL
// ─────────────────────────────────────────────────────────────────────────────
function TrustDeedModal({ onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark flex flex-col max-h-[90vh]">
        <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-navy text-white rounded-xl flex items-center justify-center shadow-sm">
              <SafeIcon icon={FiShield} />
            </div>
            <div>
              <h3 className="font-serif text-xl text-accent-navy">The Digital Estate Trust Deed</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Legal Preservation Guarantee</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-full text-text-muted transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>
        <div className="p-8 overflow-y-auto flex-grow">
          <div className="prose prose-sm text-text-main font-light leading-relaxed max-w-none space-y-5">
            <p><strong>1. Perpetual Preservation</strong><br />Every paid plan contribution is partially diverted to the Eternal Trust Fund. This fund is legally tasked with covering all infrastructure and security costs for your digital sanctuary indefinitely.</p>
            <p><strong>2. Digital Asset Protection</strong><br />The trust guarantees that all documents, media, and inventory records stored within the vault are protected by AES-256 encryption and will be accessible to designated heirs regardless of future technological shifts.</p>
            <p><strong>3. Transition of Care</strong><br />In the event of a change in corporate ownership, the Trust is structured to operate as an independent entity, ensuring that your loved one's legacy is never subject to commercial liquidation.</p>
            <p><strong>4. Heir Access Protocol</strong><br />Designated heirs undergo a verified identity process before accessing the estate. Multi-factor authentication and notarised digital credentials are required for release of sensitive assets.</p>
            <p><strong>5. Regulatory Compliance</strong><br />All data is stored in compliance with GDPR, CCPA, and applicable local data protection regulations. Annual third-party audits are conducted to maintain certification.</p>
          </div>
        </div>
        <div className="p-8 border-t border-brand-light bg-brand-light/30 shrink-0">
          <button onClick={onClose} className="w-full bg-accent-navy text-white py-3 rounded-xl font-black uppercase tracking-widest">
            Acknowledge Policy
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOADING & ERROR STATES
// ─────────────────────────────────────────────────────────────────────────────
function LoadingState() {
  return (
    <div className="space-y-12">
      <div className="text-center max-w-2xl mx-auto">
        <div className="h-4 bg-brand-dark/20 rounded-full w-48 mx-auto mb-6 animate-pulse" />
        <div className="h-10 bg-brand-dark/20 rounded-full w-80 mx-auto mb-4 animate-pulse" />
        <div className="h-4 bg-brand-dark/10 rounded-full w-64 mx-auto animate-pulse" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-[32px] border border-brand-dark overflow-hidden animate-pulse">
            <div className="h-44 bg-gradient-to-br from-gray-200 to-gray-300" />
            <div className="p-6 space-y-3">
              <div className="h-3 bg-gray-200 rounded-full w-3/4" />
              <div className="h-3 bg-gray-200 rounded-full w-1/2" />
              <div className="h-12 bg-gray-100 rounded-2xl mt-4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="text-center py-20 bg-white rounded-[40px] border-2 border-dashed border-red-200">
      <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
        <SafeIcon icon={FiAlertTriangle} className="w-10 h-10 text-red-400" />
      </div>
      <h4 className="font-serif text-accent-navy text-xl mb-2">Could Not Load Plans</h4>
      <p className="text-text-muted text-sm font-light mb-6 max-w-xs mx-auto">{message}</p>
      <button onClick={onRetry}
        className="inline-flex items-center gap-2 px-8 py-3 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all">
        <SafeIcon icon={FiRefreshCw} className="w-4 h-4" /> Retry
      </button>
    </div>
  );
}

export default LegacyPlanComparison;